import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  SelectChangeEvent,
  Button,
  Collapse,
  Fade,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import CollapsibleBaseBlock from './Patient/Blocks/CollapsibleBaseBlock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import APIService from '../services/APIService';
import { useAuth0 } from '@auth0/auth0-react';
import { useAccessToken } from '@features/user';
import { Result } from '../types/types';

export interface ScriptLine {
  speaker: string;
  utterance: string;
}

interface Transcript {
  transcript_id: string;
  title: string;
  date: string;
}

interface TranscriptDetail {
  transcript_id: string;
  title: string;
  script: ScriptLine[];
}

interface TranscriptsResponse {
  transcripts: Transcript[];
  count: number;
}

interface RecordingScriptProps {
  showImmediately?: boolean;
  onTranscriptChange?: (transcript: ScriptLine[]) => void;
}

export const RecordingScript: React.FC<RecordingScriptProps> = ({
  showImmediately = false,
  onTranscriptChange,
}) => {
  const { getAccessToken } = useAccessToken();
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string>('');
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState<TranscriptDetail | null>(null);
  const [isLoadingTranscripts, setIsLoadingTranscripts] = useState<boolean>(false);
  const [isLoadingTranscriptDetail, setIsLoadingTranscriptDetail] = useState<boolean>(false);
  const [speakers, setSpeakers] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(showImmediately);
  const [customizationDetails, setCustomizationDetails] = useState<string>('');
  const [isCustomizing, setIsCustomizing] = useState<boolean>(false);
  const [customizedScriptLines, setCustomizedScriptLines] = useState<ScriptLine[]>([]);
  const [showCustomized, setShowCustomized] = useState<boolean>(false);
  const [showCustomizationInput, setShowCustomizationInput] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Fetch available transcripts
  useEffect(() => {
    const fetchTranscripts = async () => {
      setIsLoadingTranscripts(true);
      setError('');

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('No access token available');
          setError('Authentication error. Please try again.');
          setIsLoadingTranscripts(false);
          return;
        }

        const response = await APIService.makeAPIGetRequest({
          requestString: '/v2/provider/onboarding/transcripts',
          accessToken,
        });

        if (response.ok && response.value.transcripts && response.value.transcripts.length > 0) {
          setTranscripts(response.value.transcripts);
          // Set the first transcript as selected by default
          setSelectedTranscriptId(response.value.transcripts[0].transcript_id);
        } else {
          setError('No transcripts available.');
        }
      } catch (error) {
        console.error('Error fetching transcripts:', error);
        setError('Failed to load transcripts. Please try again later.');
      } finally {
        setIsLoadingTranscripts(false);
      }
    };

    if (isExpanded) {
      fetchTranscripts();
    }
  }, [getAccessToken, isExpanded]);

  // Fetch transcript detail when selectedTranscriptId changes
  useEffect(() => {
    const fetchTranscriptDetail = async () => {
      if (!selectedTranscriptId) return;

      setIsLoadingTranscriptDetail(true);
      setError('');

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('No access token available');
          setError('Authentication error. Please try again.');
          setIsLoadingTranscriptDetail(false);
          return;
        }

        const response = await APIService.makeAPIGetRequest({
          requestString: `/v2/provider/onboarding/transcripts/${selectedTranscriptId}`,
          accessToken,
        });

        if (response.ok && response.value) {
          // Handle API response which contains utterances instead of script
          if (response.value.utterances) {
            // Map utterances to the expected script format
            const scriptLines = response.value.utterances.map((utterance: any) => ({
              speaker: utterance.speaker || utterance.role || 'Unknown',
              utterance: utterance.text || utterance.content || '',
            }));

            // Create a new object with the expected format
            setCurrentTranscript({
              transcript_id: response.value.transcript_id,
              title: response.value.title || 'Untitled',
              script: scriptLines,
            });
          } else {
            setError('Invalid transcript format received.');
          }
        } else {
          setError('Failed to load transcript details.');
        }
      } catch (error) {
        console.error('Error fetching transcript detail:', error);
        setError('Failed to load transcript details. Please try again later.');
      } finally {
        setIsLoadingTranscriptDetail(false);
      }
    };

    if (selectedTranscriptId) {
      fetchTranscriptDetail();
    }
  }, [selectedTranscriptId, getAccessToken]);

  const handleTranscriptChange = (event: SelectChangeEvent) => {
    setSelectedTranscriptId(event.target.value);
    setShowCustomized(false);
    setCustomizedScriptLines([]);
    setShowCustomizationInput(false);
    setCustomizationDetails('');
  };

  const handleCustomizationDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomizationDetails(event.target.value);
  };

  const handleCustomizeButtonClick = () => {
    setShowCustomizationInput(true);
  };

  const handleCustomizationSubmit = () => {
    handleCustomizeScript();
    setShowCustomizationInput(false);
  };

  const handleCustomizeScript = async () => {
    if (!customizationDetails || !currentTranscript?.script) return;
    setIsCustomizing(true);
    setShowCustomized(true);
    setCustomizedScriptLines([]);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        console.error('No access token available');
        setIsCustomizing(false);
        return;
      }

      await APIService.makeStreamingRequest({
        requestString: '/v2/provider/onboarding/customizeScript',
        accessToken,
        body: {
          description: customizationDetails,
          text: JSON.stringify(currentTranscript.script),
        },
        onChunk: chunk => {
          try {
            const line: ScriptLine = JSON.parse(chunk);
            setCustomizedScriptLines(prev => [...prev, line]);
          } catch (e) {
            console.error('Failed to parse script line chunk:', chunk, e);
          }
        },
        onError: error => {
          console.error('Error customizing script:', error);
          setIsCustomizing(false);
        },
        onComplete: () => {
          setIsCustomizing(false);
        },
      });
    } catch (error) {
      console.error('Error initiating customization:', error);
      setIsCustomizing(false);
    }
  };

  const showContent = () => {
    setIsExpanded(true);
  };

  useEffect(() => {
    if (!currentTranscript) return;

    const scriptToUse = showCustomized ? customizedScriptLines : currentTranscript.script;

    const uniqueSpeakers = Array.from(new Set(scriptToUse.map(line => line.speaker)));
    setSpeakers(uniqueSpeakers);
  }, [currentTranscript, showCustomized, customizedScriptLines]);

  // Add this useEffect to notify parent of transcript changes
  useEffect(() => {
    if (onTranscriptChange && currentTranscript) {
      const scriptToUse = showCustomized ? customizedScriptLines : currentTranscript.script;
      onTranscriptChange(scriptToUse);
    }
  }, [currentTranscript, showCustomized, customizedScriptLines, onTranscriptChange]);

  const renderContent = () => {
    if (isLoadingTranscripts) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (!isExpanded) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Not sure what to record for your first encounter?
            </Typography>
            <Button
              onClick={showContent}
              variant="contained"
              color="primary"
              sx={{ borderRadius: '0.5rem' }}
            >
              Show Examples
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Fade in={isExpanded} timeout={600}>
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
                pb: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  mt: 1,
                }}
              >
                <FormControl sx={{ flexGrow: 1 }}>
                  <InputLabel>Select Scenario</InputLabel>
                  <Select
                    value={selectedTranscriptId}
                    onChange={handleTranscriptChange}
                    label="Select Scenario"
                    sx={{
                      borderRadius: '0.5rem',
                      backgroundColor: 'background.paper',
                      height: 32,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'borderColors.primary',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '0.5rem',
                          mt: 0.5,
                        },
                      },
                    }}
                  >
                    {transcripts.map(transcript => (
                      <MenuItem key={transcript.transcript_id} value={transcript.transcript_id}>
                        {transcript.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Customize Script">
                  <IconButton
                    onClick={handleCustomizeButtonClick}
                    color="primary"
                    disabled={isCustomizing || !currentTranscript}
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Collapse in={showCustomizationInput} timeout={400} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tell us about you"
                    value={customizationDetails}
                    onChange={handleCustomizationDetailsChange}
                    placeholder="Describe your patient population, focus, etc."
                    variant="outlined"
                    onKeyDown={e => e.key === 'Enter' && handleCustomizationSubmit()}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCustomizationSubmit}
                    disabled={!customizationDetails || isCustomizing}
                  >
                    Submit
                  </Button>
                </Box>
              </Collapse>
            </Box>

            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {isLoadingTranscriptDetail && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              )}

              {isCustomizing && customizedScriptLines.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              )}

              {currentTranscript && !isLoadingTranscriptDetail && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mt: isCustomizing ? 2 : 0,
                  }}
                >
                  {(showCustomized ? customizedScriptLines : currentTranscript.script).map(
                    (line, index) => (
                      <Fade key={index} in={true} timeout={500}>
                        <Paper elevation={1} sx={{ p: 1 }}>
                          <Typography
                            variant="subtitle2"
                            color={speakers.indexOf(line.speaker) === 0 ? 'primary' : 'secondary'}
                            sx={{ mb: 0.5 }}
                          >
                            {line.speaker}
                          </Typography>
                          <Typography variant="body1">{line.utterance}</Typography>
                        </Paper>
                      </Fade>
                    ),
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    );
  };

  return (
    <CollapsibleBaseBlock defaultCollapsed={false}>
      <CollapsibleBaseBlock.Header title="Example Scripts" />
      <CollapsibleBaseBlock.Body>{renderContent()}</CollapsibleBaseBlock.Body>
    </CollapsibleBaseBlock>
  );
};
